import { supabase } from '../supabase';

// ── Connection codes (guardian-side) ────────────────────────────────────
// Guardian-generated, athlete-specific, single-use, 48-hour codes — the
// only path an athlete ever joins a team roster. Generating and sharing
// a code IS the guardian's consent; there is no separate confirmation
// step after a coach redeems it. See migration 012 for the full model.

/** Returns { code, expires_at } — code is the PLAINTEXT value, shown to
 * the guardian exactly once. Regenerating invalidates any previous
 * active code for this athlete. */
export async function generateConnectionCode(athleteId) {
  const { data, error } = await supabase.rpc('generate_athlete_connection_code', {
    p_athlete_id: athleteId,
  });
  if (error) throw error;
  return data?.[0] || null;
}

export async function revokeConnectionCode(athleteId) {
  const { error } = await supabase.rpc('revoke_athlete_connection_code', {
    p_athlete_id: athleteId,
  });
  if (error) throw error;
}

/** Whether an active (unredeemed, unexpired) code currently exists for
 * this athlete, and when it expires — deliberately NOT the code_hash or
 * anything else. Lets the UI show "you have an active code, expiring…"
 * across a page refresh without ever re-displaying the plaintext (which
 * only ever exists in the generate response, once). */
export async function getActiveConnectionCodeStatus(athleteId) {
  const { data, error } = await supabase
    .from('athlete_connection_codes')
    .select('id, created_at, expires_at')
    .eq('athlete_id', athleteId)
    .eq('status', 'active')
    .gt('expires_at', new Date().toISOString())
    .maybeSingle();
  if (error) throw error;
  return data;
}

// ── Redemption (coach-side) ──────────────────────────────────────────────
// Team Admin/Head Coach only — enforced server-side regardless of what the
// client sends. Always connects an EXISTING athlete; never creates one.
export async function redeemConnectionCode(teamId, code) {
  const { data, error } = await supabase.rpc('redeem_athlete_connection_code', {
    p_team_id: teamId,
    p_code: code,
  });
  if (error) throw error;
  return data;
}

// ── Roster management ─────────────────────────────────────────────────

/** Full roster — active and inactive rows, so removal history stays
 * visible (status is shown, not hidden). Embeds the athlete's core
 * profile + canonical school district name via the same FK-join pattern
 * athleteService.getAthlete() uses; RLS (012) is what actually makes
 * this readable for team staff of a rostered athlete, not this query. */
export async function listTeamRoster(teamId) {
  const { data, error } = await supabase
    .from('team_athletes')
    .select('*, athlete:athletes(id, first_name, last_name, grade, status, school_district:school_districts(name))')
    .eq('team_id', teamId)
    .order('status', { ascending: true })
    .order('added_at', { ascending: false });
  if (error) throw error;
  return data;
}

/** Single roster row with the full rostered-athlete detail needed for
 * the coach-facing view — county/school_district names embedded the
 * same way getAthlete() does for the guardian-facing profile.
 *
 * Phase C.8 — also embeds this membership's own Team -> Division ->
 * Season chain (same shape listAthleteTeamMemberships() already embeds,
 * 047) so the coach-facing AthleteCard can resolve a season label AND a
 * concrete season_id for the Card-photo lookup, without a second query
 * or duplicating this join elsewhere. Readable under existing RLS with
 * no change: the caller viewing this page is already team staff of
 * team_id (how RLS let them reach this row at all), and divisions/
 * seasons (036) are already readable by any authenticated user. */
export async function getRosteredAthlete(teamAthleteId) {
  const { data, error } = await supabase
    .from('team_athletes')
    .select(`
      *,
      athlete:athletes(*, county:counties(name), school_district:school_districts(name)),
      team:teams(id, team_name, division:divisions(id, name, season:seasons(id, name, year)))
    `)
    .eq('id', teamAthleteId)
    .single();
  if (error) throw error;
  return data;
}

export async function removeFromRoster(teamAthleteId) {
  const { data, error } = await supabase.rpc('remove_team_athlete', {
    p_team_athlete_id: teamAthleteId,
  });
  if (error) throw error;
  return data;
}

/** Superseded by updateRosterDetails() below (Phase C.6) — kept live,
 * unused from this app going forward, same "old function stays, new one
 * takes over" pattern already used for generate_team_join_code() ->
 * generate_team_join_code_labeled(). */
export async function updateRosterPosition(teamAthleteId, { primaryPosition, secondaryPosition }) {
  const { data, error } = await supabase.rpc('update_team_athlete_position', {
    p_team_athlete_id: teamAthleteId,
    p_primary_position: primaryPosition || null,
    p_secondary_position: secondaryPosition || null,
  });
  if (error) throw error;
  return data;
}

/** Phase C.6 — the one roster-edit action an authorized Team Admin/Head
 * Coach uses to set/correct a membership's seasonal context: positions
 * (same as updateRosterPosition() above), jersey number (nullable, no
 * uniqueness — duplicates are allowed by design), and season_grade — the
 * athlete's actual school grade DURING this specific membership, never a
 * live reference to the athlete's current grade. Always sends all four
 * fields together (matching update_team_athlete_roster_details()'s own
 * "one cohesive edit" contract, not four independent partial updates).
 * jerseyNumber is passed through as a real number or null — 0 is a
 * valid jersey number and must never be treated as "clear". */
export async function updateRosterDetails(teamAthleteId, { primaryPosition, secondaryPosition, jerseyNumber, seasonGrade }) {
  const { data, error } = await supabase.rpc('update_team_athlete_roster_details', {
    p_team_athlete_id: teamAthleteId,
    p_primary_position: primaryPosition || null,
    p_secondary_position: secondaryPosition || null,
    p_jersey_number: jerseyNumber === '' || jerseyNumber === undefined ? null : jerseyNumber,
    p_season_grade: seasonGrade || null,
  });
  if (error) throw error;
  return data;
}

// ── Guardian view of an athlete's team memberships ──────────────────────
/** Phase C.6 — extended to embed each membership's Division/Season
 * context (team.division.season) so the Football History view can
 * group by Season and optionally show Division for disambiguation,
 * without a second query or a duplicated display string anywhere.
 * Readable under existing RLS with no change: seasons/divisions (036)
 * already grant SELECT to any authenticated user unconditionally, and
 * teams is already readable here via the existing guardian/staff
 * policies (012) — this is a pure select-projection widening, not a
 * new access path. */
export async function listAthleteTeamMemberships(athleteId) {
  const { data, error } = await supabase
    .from('team_athletes')
    .select('*, team:teams(id, team_name, season, grade_age_group, status, division:divisions(id, name, season:seasons(id, name, year)))')
    .eq('athlete_id', athleteId)
    .order('status', { ascending: true })
    .order('added_at', { ascending: false });
  if (error) throw error;
  return data;
}

// ═════════════════════════════════════════════════════════════════════
// Team join codes — the primary V1 onboarding path: a coach shares a
// multi-use code with a group of families (e.g. equipment handout,
// parent meeting, late registration). A team may have MULTIPLE
// simultaneously valid codes (016) — generating a new one never revokes
// an older one; each code only stops working on its own 14-day
// expiration or an explicit, per-code revoke. Possessing a code alone
// never creates roster membership — it only ever lets a guardian submit
// a request (below), which still needs an explicit Team Admin/Head Coach
// approval. A code's plaintext can be recovered later via Vault (017),
// on demand, one code at a time, only by staff authorized to manage that
// team — see revealTeamJoinCode() below. See migrations 012, 016, 017.
// ═════════════════════════════════════════════════════════════════════

/** Returns { code, expires_at } — plaintext shown once, same discipline
 * as the athlete code (never persisted, never re-displayable after this
 * call). Does NOT invalidate any other currently-active code for this
 * team (016) — generating another code is always additive. `label` is
 * optional, purely descriptive (e.g. "Equipment Handout"), and has no
 * authorization meaning — pass null/undefined/'' for an unlabeled code.
 * Calls generate_team_join_code_labeled (016), not the older, unlabeled
 * generate_team_join_code RPC, which stays live server-side untouched
 * but is no longer called from this app. Team Admin/Head Coach only
 * (enforced server-side). */
export async function generateTeamJoinCode(teamId, label) {
  const { data, error } = await supabase.rpc('generate_team_join_code_labeled', {
    p_team_id: teamId,
    p_label: label || null,
  });
  if (error) throw error;
  return data?.[0] || null;
}

/** Revokes ONE specific join code by its own id (016) — never "the
 * team's" code, since a team can have several valid at once. Calls
 * revoke_team_join_code_by_id (016); the older, team-scoped
 * revoke_team_join_code RPC is locked down server-side (would revoke
 * EVERY active code for the team at once under the multi-code model) and
 * is no longer called from this app. Server-side re-derives the code's
 * team from p_team_join_code_id and checks the caller's authority
 * against THAT team; a caller can't revoke a code belonging to a team
 * they don't manage by guessing its id. */
export async function revokeTeamJoinCode(teamJoinCodeId) {
  const { error } = await supabase.rpc('revoke_team_join_code_by_id', {
    p_team_join_code_id: teamJoinCodeId,
  });
  if (error) throw error;
}

/** All of this team's currently valid (active, unexpired) join codes —
 * potentially several (016), most recent first, each with its optional
 * label. Never the plaintext code, only metadata (id, label, created_at,
 * expires_at) plus code_secret_id (017) — same discipline as
 * getActiveConnectionCodeStatus. code_secret_id is not itself sensitive
 * (it's an opaque pointer into Vault, unreadable by any client role
 * without going through revealTeamJoinCode() below and passing its
 * authorization check) — its presence/absence is what lets the UI show a
 * disabled "not available" state for legacy codes generated before
 * reveal support existed, instead of only discovering that on a failed
 * reveal attempt. Team Admin/Head Coach can read team_join_codes
 * directly under RLS (unlike athlete codes, which block all direct
 * SELECT). */
export async function listActiveTeamJoinCodes(teamId) {
  const { data, error } = await supabase
    .from('team_join_codes')
    .select('id, label, created_at, expires_at, code_secret_id')
    .eq('team_id', teamId)
    .eq('status', 'active')
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

/** Returns the PLAINTEXT of one specific code, fetched on demand — never
 * bulk, never prefetched for a whole list (017). Only Team Admin/Head
 * Coach of that code's own team, or Platform Admin, can call this
 * successfully; server-side re-derives the team from the code itself,
 * the same pattern as revokeTeamJoinCode(). Raises a clear error for a
 * legacy code that predates reveal support (code_secret_id is null on
 * the row) — callers should check listActiveTeamJoinCodes()'s
 * code_secret_id first to show a disabled state proactively rather than
 * relying on this throwing. The caller is responsible for keeping the
 * result in memory only (component state) — this function does not
 * persist it anywhere, and neither should any caller. */
export async function revealTeamJoinCode(teamJoinCodeId) {
  const { data, error } = await supabase.rpc('reveal_team_join_code_by_id', {
    p_team_join_code_id: teamJoinCodeId,
  });
  if (error) throw error;
  return data;
}

/** Builds the shareable invite URL for a plaintext code (017) — uses the
 * page's actual live origin (not a hardcoded domain) so it's correct in
 * every environment (production, localhost, any preview deploy) rather
 * than risking a stale/mismatched hardcoded host. */
export function buildJoinInviteLink(code) {
  return `${window.location.origin}/join/${code}`;
}

/** Guardian-side: look up team identity by code BEFORE choosing an
 * athlete or submitting anything — shows what the guardian is about to
 * request to join. Any signed-in user may call this; the code itself is
 * the gate, and team identity is non-sensitive. */
export async function previewTeamJoinCode(code) {
  const { data, error } = await supabase.rpc('preview_team_join_code', { p_code: code });
  if (error) throw error;
  return data?.[0] || null;
}

/** Guardian-side: submits a roster request for ONE of their own actively-
 * guarded athletes. Never creates team_athletes directly — only a
 * pending team_roster_requests row, requiring Team Admin/Head Coach
 * approval. */
export async function requestTeamRosterJoin(code, athleteId) {
  const { data, error } = await supabase.rpc('request_team_roster_join', {
    p_code: code,
    p_athlete_id: athleteId,
  });
  if (error) throw error;
  return data;
}

/** Coach-side: pending + historical requests for a team, with the
 * athlete fields the Roster Requests UI needs plus which invitation
 * produced each request (invite_label — 017) — via the
 * list_team_roster_requests_with_source() RPC (017), not a direct table
 * embed, since a still-pending request's athlete isn't yet covered by
 * the athletes SELECT policy (that stays scoped to active roster
 * membership only). invite_label is null for a legacy request with no
 * captured attribution (never fabricated) — the UI should omit the
 * "Invite:" line in that case, not show a placeholder. The older,
 * unlabeled list_team_roster_requests() RPC (012) stays live server-side
 * untouched but is no longer called from this app. Never returns the
 * requesting guardian's identity. */
export async function listTeamRosterRequests(teamId) {
  const { data, error } = await supabase.rpc('list_team_roster_requests_with_source', { p_team_id: teamId });
  if (error) throw error;
  return data;
}

/** Guardian-side: pending + historical requests for one athlete, with
 * team identity embedded — readable directly under RLS since the new
 * guardian-facing teams policy (012) covers any team the athlete has a
 * request (of any status) with. */
export async function listAthleteRosterRequests(athleteId) {
  const { data, error } = await supabase
    .from('team_roster_requests')
    .select('*, team:teams(id, team_name, season, grade_age_group)')
    .eq('athlete_id', athleteId)
    .order('requested_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function approveRosterRequest(requestId) {
  const { data, error } = await supabase.rpc('approve_team_roster_request', { p_request_id: requestId });
  if (error) throw error;
  return data;
}

export async function declineRosterRequest(requestId) {
  const { data, error } = await supabase.rpc('decline_team_roster_request', { p_request_id: requestId });
  if (error) throw error;
  return data;
}

export async function cancelRosterRequest(requestId) {
  const { data, error } = await supabase.rpc('cancel_team_roster_request', { p_request_id: requestId });
  if (error) throw error;
  return data;
}
